import {useCallback, useRef, useState} from "react";

/**
 * Builds a stable suppression key for a given section + original record.
 * Any field whose current value matches the key is auto-skipped next time.
 *
 * @param {string} sectionKey
 * @param {Object} apiRequest
 * @returns {string}
 */
const makeSuppressKey = (sectionKey, apiRequest) => {
    const stringFields = Object.fromEntries(
        Object.entries(apiRequest ?? {}).filter(([, v]) => typeof v === 'string')
    );
    return `${sectionKey}::${JSON.stringify(stringFields)}`;
};

/**
 * Extracts a comparable scalar value from a raw field value.
 *
 * Rules (applied in order):
 *   1. If the field declares an `extractValue` function → call it and return the result.
 *   2. If the value is already a scalar (string / number / boolean / null / undefined) → return as-is.
 *   3. If the value is an object with no extractor → return undefined
 *      (modifyRecord will still receive the full object, but the value cannot be used for comparisons).
 *
 * @param {import('../../engine/corrective-interactions').InteractiveField} field
 * @param {any} value  - Raw value from the dialog (may be an autocomplete object or a scalar)
 * @returns {any}      - The extracted scalar, or undefined if extraction is not possible
 */
const resolveFieldScalar = (field, value) => {
    if (typeof field.extractValue === 'function') {
        return field.extractValue(value);
    }
    // Already a scalar — primitives and null pass through directly
    if (value === null || value === undefined || typeof value !== 'object') {
        return value;
    }
    // Object with no extractor declared — cannot derive a reliable scalar
    return undefined;
};

/**
 * useInteractiveCorrection
 *
 * Manages the interactive error-correction lifecycle during bulk import:
 *  - Determines which InteractiveSectionConfigs are triggered by a failed response.
 *  - Pauses the import loop by returning a Promise that only resolves after user action.
 *  - On "Apply and Retry", builds an updated record and optionally propagates
 *    the same field mutations to all remaining (future) records that share the
 *    same original values.
 *  - On "Proceed without changes", optionally records a suppression key so that
 *    the same error pattern is silently skipped for the rest of this session.
 *
 * @param {Object}  opts
 * @param {Array}   opts.interactiveSections  - InteractiveSectionConfig[] for the current data type
 * @param {Object}  opts.currentIndexRef      - React ref holding the loop's current index (int)
 * @param {Function} opts.setApiRequests      - Updater fn for apiRequests in the parent view
 * @param {Object}  opts.staticDataContext    - Pre-loaded bundle { allCategories, allIndustries, industryId, … }
 */
const useInteractiveCorrection = ({
                                      interactiveSections,
                                      currentIndexRef,
                                      setApiRequests,
                                      staticDataContext,
                                      getExternalEnabled,
                                  }) => {
    /** @type {null | { apiRequest: Object, triggeredSections: Array }} */
    const [dialogState, setDialogState] = useState(null);

    /** Holds the Promise resolver so the import loop can await user action */
    const resolveCorrectionRef = useRef(null);


    /**
     * Persistent suppression registry for this import session.
     * Keys are built by makeSuppressKey and added when the user chooses
     * "Proceed without changes" with suppressSimilar checked.
     */
    const suppressionSet = useRef(new Set());

    // ─── public API ──────────────────────────────────────────────────────────

    /**
     * Called from processRequest right after a failed API response.
     *
     * 1. Filters sections to those triggered by response.errorCode and not suppressed.
     * 2. Pre-computes fieldOptions synchronously from staticDataContext.
     * 3. Returns a Promise that resolves to { action: 'retry'|'skip', updatedRecord? }
     *    or null if interactive correction should not trigger.
     *
     * @param {{ apiRequest: Object, response: Object }} params
     * @returns {Promise<{action: string, updatedRecord?: Object}> | null}
     */
    const requestCorrectionIfNeeded = useCallback(({apiRequest, response}) => {
        if (!getExternalEnabled() || !interactiveSections?.length) return null;

        const triggered = interactiveSections.filter(
            (s) =>
                s.triggerErrorCodes.includes(response?.errorCode) &&
                !suppressionSet.current.has(makeSuppressKey(s.sectionKey, apiRequest))
        );

        if (!triggered.length) return null;

        // Pre-compute options synchronously (no async — data already loaded)
        const sectionsWithOptions = triggered.map((s) => ({
            ...s,
            fieldOptions: s.getFieldOptions?.(apiRequest, staticDataContext ?? {}),
        }));

        return new Promise((resolve) => {
            resolveCorrectionRef.current = resolve;
            setDialogState({apiRequest, triggeredSections: sectionsWithOptions});
        });
    }, [getExternalEnabled, interactiveSections, staticDataContext]);

    /**
     * Called by the dialog's "Apply and Retry" button.
     *
     * Execution order:
     *   1. Build updatedRecord by running each section's field.modifyRecord in order.
     *   2. If applyToSimilar: iterate remaining apiRequests (indices after currentIndex)
     *      and apply the same mutations to records whose original field value matches
     *      the failing record's original value.
     *   3. Close dialog and resolve the awaited Promise.
     *
     * @param {Object} params
     * @param {{ [sectionKey: string]: { [fieldKey: string]: any } }} params.fieldValuesBySectionKey
     * @param {boolean} params.applyToSimilar
     * @param {Object}  params.originalApiRequest
     */
    const applyAndRetry = useCallback(({fieldValuesBySectionKey, applyToSimilar, originalApiRequest}) => {
        const triggeredSections = dialogState?.triggeredSections ?? [];

        // 1. Build the patched record for the current failing entry.
        //    resolveFieldScalar converts autocomplete objects (or any raw value) to the
        //    scalar the field expects before handing it to modifyRecord.
        let updatedRecord = {...originalApiRequest};
        for (const section of triggeredSections) {
            const vals = fieldValuesBySectionKey[section.sectionKey] ?? {};
            for (const field of section.fields) {
                if (vals[field.key] !== undefined) {
                    const scalar = resolveFieldScalar(field, vals[field.key]);
                    updatedRecord = field.modifyRecord(updatedRecord, scalar);
                }
            }
        }

        // 2. Propagate mutations to future records (if the user opted in).
        //    extractedNewVal is the scalar we resolved for the user's selection;
        //    we compare it against the future record's field value to decide whether
        //    this particular record also had the "old" (failing) value and should be
        setApiRequests((prev) => {
            const next = [...prev];
            const currentIdx = currentIndexRef.current ?? 0;

            // 1. Always update the current record in state so that if it still fails (or is skipped later),
            // the UI and exports report the newly selected values.
            if (next[currentIdx]) {
                next[currentIdx] = updatedRecord;
            }

            // 2. Propagate mutations to future records (if the user opted in).
            if (applyToSimilar) {
                const startIdx = currentIdx + 1;
                for (let i = startIdx; i < next.length; i++) {
                    if (!next[i]) continue;
                    let rec = {...next[i]};
                    const pristineOriginal = next[i];
                    for (const section of triggeredSections) {
                        const vals = fieldValuesBySectionKey[section.sectionKey] ?? {};

                        // Extract fields the user actually interacted with for this section
                        const modifiedFields = section.fields.filter(f => vals[f.key] !== undefined);
                        if (!modifiedFields.length) continue;

                        let isSimilar = false;
                        if (typeof section.isSimilarRecord === 'function') {
                            // Let the section dictate its own complex matching logic (A AND B, A OR B, etc.)
                            isSimilar = section.isSimilarRecord({
                                futureRecord: pristineOriginal,
                                failingRecord: originalApiRequest,
                                userValues: vals
                            });
                        } else {
                            // Default behavior: Logical AND for all fields the user modified.
                            // If they changed A and B, the future record must match both A and B of the failing record.
                            isSimilar = modifiedFields.every(
                                f => pristineOriginal[f.key] === originalApiRequest[f.key]
                            );
                        }

                        // If the record is similar, apply this section's fields atomically
                        if (isSimilar) {
                            for (const field of modifiedFields) {
                                const scalar = resolveFieldScalar(field, vals[field.key]);
                                rec = field.modifyRecord(rec, scalar);
                            }
                        }
                    }
                    next[i] = rec;
                }
            }

            return next;
        });

        // 3. Resolve and close
        setDialogState(null);
        resolveCorrectionRef.current?.({action: 'retry', updatedRecord});
    }, [dialogState, currentIndexRef, setApiRequests]);

    /**
     * Called by the dialog's "Proceed without changes" button.
     *
     * If suppressSimilar is true, adds a suppression key so that the exact same
     * section + record-value combination is never prompted again for this session.
     *
     * @param {Object}  params
     * @param {boolean} params.suppressSimilar
     * @param {Object}  params.originalApiRequest
     */
    const skip = useCallback(({suppressSimilar, originalApiRequest}) => {
        const triggeredSections = dialogState?.triggeredSections ?? [];

        if (suppressSimilar) {
            for (const section of triggeredSections) {
                suppressionSet.current.add(makeSuppressKey(section.sectionKey, originalApiRequest));
            }
        }

        setDialogState(null);
        resolveCorrectionRef.current?.({action: 'skip'});
    }, [dialogState]);

    return {
        /** null when closed; { apiRequest, triggeredSections } when the dialog is open */
        dialogState,
        /** Call from processRequest after a failed response */
        requestCorrectionIfNeeded,
        /** Callback for the dialog's "Apply and Retry" action */
        applyAndRetry,
        /** Callback for the dialog's "Proceed without changes" action */
        skip,
    };
};

export default useInteractiveCorrection;
